// NAV-MODEL: single path navigation. Thirteen top level categories (Home,
// Ghost, Messages, Schedule, Sales, Customers, Money, Website, Marketing,
// Automations, Team, Settings, and Admin when isAdmin), each with one entry
// per destination. Both the sidebar and the top strip render from this single
// SECTIONS export; this wave changes only the shape and order of the tree, not
// any route path.
//
// Design rule (MASTER-REDESIGN-PLAN.md and the nav audit): group by the
// merchant's mental model, order by how often a home service owner opens each
// thing, one home per object, scope switching lives in the account switcher.

import Boxes from 'lucide-react/dist/esm/icons/boxes'
import { sectionIsHidden } from './deadSections'
import Building2 from 'lucide-react/dist/esm/icons/building-2'
import Banknote from 'lucide-react/dist/esm/icons/banknote'
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3'
import BookOpen from 'lucide-react/dist/esm/icons/book-open'
import Briefcase from 'lucide-react/dist/esm/icons/briefcase'
import Calendar from 'lucide-react/dist/esm/icons/calendar'
import Camera from 'lucide-react/dist/esm/icons/camera'
import ClockIcon from 'lucide-react/dist/esm/icons/clock'
import ClipboardCheck from 'lucide-react/dist/esm/icons/clipboard-check'
import ClipboardList from 'lucide-react/dist/esm/icons/clipboard-list'
import Bot from 'lucide-react/dist/esm/icons/bot'
import Brain from 'lucide-react/dist/esm/icons/brain'
import Coins from 'lucide-react/dist/esm/icons/coins'
import Crown from 'lucide-react/dist/esm/icons/crown'
import Trophy from 'lucide-react/dist/esm/icons/trophy'
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign'
import Filter from 'lucide-react/dist/esm/icons/filter'
import HandCoins from 'lucide-react/dist/esm/icons/hand-coins'
import FileText from 'lucide-react/dist/esm/icons/file-text'
import FileSignature from 'lucide-react/dist/esm/icons/file-signature'
import Gift from 'lucide-react/dist/esm/icons/gift'
import Globe from 'lucide-react/dist/esm/icons/globe'
import Bell from 'lucide-react/dist/esm/icons/bell'
import ImageIcon from 'lucide-react/dist/esm/icons/image'
import Inbox from 'lucide-react/dist/esm/icons/inbox'
import Kanban from 'lucide-react/dist/esm/icons/kanban'
import LayoutDashboard from 'lucide-react/dist/esm/icons/layout-dashboard'
import Link2 from 'lucide-react/dist/esm/icons/link-2'
import Mail from 'lucide-react/dist/esm/icons/mail'
import MapPin from 'lucide-react/dist/esm/icons/map-pin'
import MapPinned from 'lucide-react/dist/esm/icons/map-pinned'
import Megaphone from 'lucide-react/dist/esm/icons/megaphone'
import MessageCircle from 'lucide-react/dist/esm/icons/message-circle'
import Palette from 'lucide-react/dist/esm/icons/palette'
import MessageSquare from 'lucide-react/dist/esm/icons/message-square'
import Phone from 'lucide-react/dist/esm/icons/phone'
import PhoneCall from 'lucide-react/dist/esm/icons/phone-call'
import Receipt from 'lucide-react/dist/esm/icons/receipt'
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw'
import Share2 from 'lucide-react/dist/esm/icons/share-2'
import Workflow from 'lucide-react/dist/esm/icons/workflow'
import SettingsIcon from 'lucide-react/dist/esm/icons/settings'
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check'
import Star from 'lucide-react/dist/esm/icons/star'
import Truck from 'lucide-react/dist/esm/icons/truck'
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up'
import Upload from 'lucide-react/dist/esm/icons/upload'
import Users from 'lucide-react/dist/esm/icons/users'
import Wallet from 'lucide-react/dist/esm/icons/wallet'
import WalletMinimal from 'lucide-react/dist/esm/icons/wallet-minimal'
import Wand2 from 'lucide-react/dist/esm/icons/wand-2'
import Store from 'lucide-react/dist/esm/icons/store'
import type { CapabilityOverrides, TeamRole } from './roles'
import { can } from './roles'
import { features, sitesBuilderVisible } from './features'
import { vocabWord, type Vocabulary, type VocabNoun } from './vocabulary'
import { listPathForType, navNodeIdForType } from './recordTypes'

// ---------- types ----------

// Wave F4 NAMEPLATE: a nav label/action bound to a merchant-renamable noun.
// When a node carries one of these, the nav renderer swaps in the merchant's
// word; absent = the literal label below, byte-identical to today. `template`
// carries a {w} placeholder for surrounding copy (e.g. 'Open {w}', 'Public
// {w}'); it defaults to '{w}'.
export interface NavVocabBinding {
  noun: VocabNoun
  plural: boolean
  capital: boolean
  template?: string
}

export interface NavChild {
  /**
   * PLATFORM-1 — STABLE IDENTITY, and everything in this wave depends on it.
   *
   * Before this, a child was identified ONLY by its route. An account
   * override keyed on a route silently orphans the moment that route changes,
   * and the merchant's hidden module quietly comes back. So every child now
   * carries an id DERIVED FROM ITS PURPOSE - its section plus its label as
   * written - and NEVER from its route.
   *
   * IT NEVER CHANGES ONCE SHIPPED. Renaming the label does not change the id;
   * moving the route does not change the id. A test fails if any id is
   * missing, duplicated, or looks like a route path.
   */
  id: string
  // Display label, e.g. "Pipeline".
  label: string
  // One-sentence plain-language description for the section
  // landing card. Written for the team owner, not the developer.
  description: string
  // Route to navigate to when the user clicks the row or card.
  to: string
  icon: React.ComponentType<{ className?: string }>
  // Optional accent style consumed by both sidebars. 'ai' tints
  // links purple (matches AI Studio), 'admin' is the same purple
  // for the platform admin section.
  accent?: 'ai' | 'admin'
  // Action button text shown on the section landing card. When
  // omitted the card body is clickable but no explicit CTA is
  // rendered.
  primaryActionLabel?: string
  // Where the action button navigates. Defaults to `to` when
  // omitted but the explicit field exists for cases like
  // "+ Import" that should drop the user one level deeper than
  // the card itself.
  primaryActionTo?: string
  // Receives the caller's TeamRole and is_admin flag. Returning
  // false hides the child from sidebars AND from the section
  // landing page card grid.
  gate?: (
    role: TeamRole | null,
    isAdmin: boolean,
    overrides?: CapabilityOverrides | null,
  ) => boolean
  // Polled count key for the small red badge pill (Phase 36
  // pattern). Today the only known key is the
  // unconverted-Won-opportunities counter.
  badgeKey?: 'unconverted_won_opportunities'
  // True for routes that exist as stubs ("Coming soon" treatment
  // on the landing card; PlaceholderPage rendered at the route).
  isPlaceholder?: boolean
  // Renders as <a target="_blank"> rather than a Router NavLink.
  external?: boolean
  // Audit C8: routes whose own page header already surfaces this
  // child's action. On those routes the tab strip does not count this
  // child toward its render decision (kills duplicated chrome like
  // Smart import appearing in the strip AND the page header).
  surfacedOnRoutes?: string[]
  // Wave F4 NAMEPLATE: bind this row's `label` to a merchant noun. Absent =
  // the literal label. Applied at render by applyVocabularyToSections.
  vocab?: NavVocabBinding
  // Wave F4 NAMEPLATE: bind `primaryActionLabel` to a merchant noun.
  actionVocab?: NavVocabBinding
  // UX-3: optional search synonyms ("related terms") so a page is findable
  // by words other than its label, e.g. Quotes <- 'estimate, bid'. Matched
  // alongside the displayed label by the sidebar search.
  keywords?: string[]
}

export interface NavSection {
  // Stable slug used for path matching and as the React key.
  id: string
  // Display label, e.g. "Sales".
  label: string
  // Plain-language description shown at the top of the section
  // landing page and as a tooltip on collapsed sidebar variants.
  description: string
  // The section landing route. Clicking the section row in the
  // sidebar navigates here; the page renders SectionLandingPage
  // with this section's children as cards.
  to: string
  icon: React.ComponentType<{ className?: string }>
  // Optional accent style; today only Admin uses 'admin' for the
  // purple Crown treatment.
  accent?: 'ai' | 'admin'
  // Hides the entire section from sidebars and section listings
  // when it returns false. A section with NO gate but children is
  // visible when at least one child is visible (filterSectionsByRole).
  // This is how the Money and Website categories work: no section
  // capability of their own, visible if any child gate passes.
  gate?: (
    role: TeamRole | null,
    isAdmin: boolean,
    overrides?: CapabilityOverrides | null,
  ) => boolean
  // Badge key for top-level (childless) sections promoted from
  // groups (Phase 62.D). Same semantics as NavChild.badgeKey.
  badgeKey?: 'unconverted_won_opportunities'
  children: NavChild[]
  // UX-3: optional search synonyms for the section, e.g. Money <- 'payments,
  // payouts, invoices'. Matched alongside the displayed label.
  keywords?: string[]
  // VOCAB-1: bind this SECTION's `label` to a merchant noun, exactly as a
  // child does. F4 gave the binding to children only, which meant a business
  // renaming "Customers" to "Clients" got it on the child row and kept our
  // word on the section header directly above it - the two words for one
  // thing, six pixels apart.
  vocab?: NavVocabBinding
}

// ---------- sections ----------
// NAV-MODEL: thirteen categories in frequency order. Every destination has
// exactly one entry. Routes and gates are unchanged from before this wave;
// only the grouping, order, and a few labels changed.

export const SECTIONS: NavSection[] = [
  {
    id: 'home',
    keywords: ['dashboard', 'overview', 'ghost', 'chats'],
    // GHOST-IS-HOME-1 — "named Ghost rather than Home", the owner's words.
    label: 'Ghost',
    description: 'Every chat, and where you build what your business runs on.',
    to: '/dashboard',
    icon: LayoutDashboard,
    children: [],
  },
  // Ghost stays a top-level brand pillar (owner decision).
  // roles.ts has no servicebot.* capability, so this stays ungated: every
  // role sees it, matching prior behavior.
  // BASE-AND-LIBRARY-1 — APPROVALS IS NOT A NAV SECTION ANY MORE.
  //
  // It was never a place a business does work. It is a STATE: some rows are
  // unresolved. By this wave's principle, places get sections and states get
  // surfaced where the merchant already is, so it folds into Home's attention
  // stack, which already ranks by money, and Ghost names it when the panel
  // opens.
  //
  // NOTHING BECAME UNREACHABLE. /dashboard/approvals still resolves and is
  // still guarded; Home links to it; the launcher mentions it. What stopped
  // is it occupying one of a merchant's menu slots on every account forever.
  // Messages: the highest frequency surface. Inbox, then Calls, then any
  // flag-gated social channels, then the setup rows.
  {
    id: 'messages',
    keywords: ['text', 'sms', 'chat', 'conversations', 'dm', 'inbox'],
    label: 'Messages',
    description: 'Every customer conversation across SMS, email, web chat, and social.',
    to: '/dashboard/inbox',
    icon: MessageCircle,
    children: [
      {
        id: 'messages_inbox',
        label: 'Inbox',
        description: 'Unified conversations across SMS, email, social, and more.',
        to: '/dashboard/inbox',
        icon: Inbox,
        primaryActionLabel: 'Open inbox',
        gate: (role, _isAdmin, o) => can(role, 'inbox.view', { overrides: o }),
      },
      {
        // Calls are events in the unified inbox thread, so this is a quick
        // link into the voice-filtered inbox.
        id: 'messages_calls',
        label: 'Calls',
        description:
          'Voice calls in your unified inbox: voicemails, transcripts, recordings, and one click to call back.',
        to: '/dashboard/inbox?channel=voice',
        icon: PhoneCall,
        primaryActionLabel: 'Open calls',
        gate: (role, _isAdmin, o) => can(role, 'phone.calls_view', { overrides: o }),
      },
      // Flag-gated social channels slot in directly after Calls, in order.
      ...(features.whatsapp
        ? [
            {
              id: 'messages_whatsapp',
              label: 'WhatsApp',
              description:
                'View and reply to WhatsApp conversations. See 24 hour window status and use approved templates.',
              to: '/dashboard/inbox/whatsapp',
              // ROLE-GATE-1: mirrors the route capability so the nav never
              // shows a link that the page then refuses.
              gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
                can(role, 'inbox.channels_manage', { overrides: o }),
              icon: MessageCircle,
              primaryActionLabel: 'Open WhatsApp',
            },
          ]
        : []),
      ...(features.messenger
        ? [
            {
              id: 'messages_messenger',
              label: 'Messenger',
              description:
                'View and reply to Facebook Messenger conversations. 24 hour window status shown inline.',
              to: '/dashboard/inbox/messenger',
              // ROLE-GATE-1: mirrors the route capability so the nav never
              // shows a link that the page then refuses.
              gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
                can(role, 'inbox.channels_manage', { overrides: o }),
              icon: MessageSquare,
              primaryActionLabel: 'Open Messenger',
            },
          ]
        : []),
      ...(features.instagram
        ? [
            {
              id: 'messages_instagram',
              label: 'Instagram',
              description:
                'View and reply to Instagram Direct Messages. 24 hour window status shown inline.',
              to: '/dashboard/inbox/instagram',
              // ROLE-GATE-1: mirrors the route capability so the nav never
              // shows a link that the page then refuses.
              gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
                can(role, 'inbox.channels_manage', { overrides: o }),
              icon: Camera,
              primaryActionLabel: 'Open Instagram',
            },
          ]
        : []),
      {
        id: 'messages_phone_setup',
        label: 'Phone setup',
        description:
          'Get a dedicated phone line for your business so you can text and call customers from your own number.',
        to: '/dashboard/inbox/phone-setup',
        keywords: ['number', 'a2p', 'texting'],
        icon: Phone,
        primaryActionLabel: 'Set up your number',
        gate: (role, _isAdmin, o) => can(role, 'phone.setup_manage', { overrides: o }),
      },
      {
        id: 'messages_chat_widget',
        label: 'Chat widget',
        description:
          'Customize the embeddable web chat widget that appears on your public profile and your own website.',
        to: '/dashboard/inbox/widget-settings',
        icon: MessageSquare,
        primaryActionLabel: 'Open widget settings',
        gate: (role, _isAdmin, o) => can(role, 'inbox.channels_manage', { overrides: o }),
      },
      {
        id: 'messages_email_domain',
        label: 'Email domain',
        description:
          'Send outbound from your own domain instead of the shared ghostcrm.io address. Higher deliverability and your brand on every message.',
        to: '/dashboard/inbox/email-domain',
        icon: Mail,
        primaryActionLabel: 'Manage email domain',
        gate: (role, _isAdmin, o) => can(role, 'inbox.channels_manage', { overrides: o }),
      },
    ],
  },
  // Schedule: time and work. The jobs board is the office home screen.
  {
    id: 'schedule',
    keywords: ['calendar', 'jobs', 'appointments', 'dispatch', 'crew'],
    label: 'Schedule',
    description: 'The jobs board, dispatch, office tasks, the time clock, and your own day.',
    to: '/dashboard/jobs',
    icon: Briefcase,
    children: [
      {
        id: 'schedule_jobs',
        label: 'Jobs',
        description: 'Schedule, assign, and complete work performed for customers.',
        to: '/dashboard/jobs',
        icon: Briefcase,
        primaryActionLabel: 'Open jobs',
        vocab: { noun: 'job', plural: true, capital: true },
        actionVocab: { noun: 'job', plural: true, capital: false, template: 'Open {w}' },
        gate: (role, _isAdmin, o) => can(role, 'jobs.view', { overrides: o }),
      },
      {
        id: 'schedule_dispatch',
        label: 'Dispatch',
        description: 'Assign jobs to techs, plan the day, and track field status in real time.',
        to: '/dashboard/operations/dispatch',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'dispatch.manage', { overrides: o }),
        icon: Truck,
        primaryActionLabel: 'Open dispatch',
      },
      {
        id: 'schedule_tasks',
        label: 'Tasks',
        description: 'Lightweight to dos: callbacks, follow ups, reminders. Separate from jobs.',
        to: '/dashboard/tasks',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'tasks.manage', { overrides: o }),
        icon: ClipboardCheck,
        primaryActionLabel: 'Open tasks',
        vocab: { noun: 'task', plural: true, capital: true },
        actionVocab: { noun: 'task', plural: true, capital: false, template: 'Open {w}' },
      },
      {
        id: 'schedule_time_clock',
        label: 'Time clock',
        description: 'Clock in when you start work, clock out when you finish.',
        to: '/dashboard/clock',
        icon: ClockIcon,
        primaryActionLabel: 'Open clock',
      },
      {
        id: 'schedule_my_schedule',
        label: 'My schedule',
        description: 'View your assigned jobs for today and update field status.',
        to: '/dashboard/operations/tech-view',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'schedule.view_own', { overrides: o }),
        icon: ClipboardList,
        primaryActionLabel: 'View schedule',
      },
    ],
  },
  // Sales: the sales object chain. Leads first, deliberately: an uncontacted
  // lead is the most time-sensitive thing in a home service business.
  {
    id: 'sales',
    keywords: ['quotes', 'estimates', 'pipeline', 'leads', 'deals', 'opportunities'],
    label: 'Sales',
    description: 'Inbound leads, quotes, the deal pipeline, door knocking, and sales analytics.',
    to: '/dashboard/leads',
    icon: Kanban,
    children: [
      {
        id: 'sales_leads',
        label: 'Leads',
        description: 'Inbound contacts captured from your pages and forms.',
        to: '/dashboard/leads',
        icon: Megaphone,
        primaryActionLabel: 'Open leads',
        vocab: { noun: 'lead', plural: true, capital: true },
        actionVocab: { noun: 'lead', plural: true, capital: false, template: 'Open {w}' },
        gate: (role, _isAdmin, o) => can(role, 'leads.view', { overrides: o }),
      },
      {
        id: 'sales_quotes',
        label: 'Quotes',
        description: 'Send price quotes, share a link, get a yes from the customer.',
        to: '/dashboard/quotes',
        keywords: ['estimate', 'bid', 'proposal'],
        icon: FileText,
        primaryActionLabel: 'Open quotes',
        vocab: { noun: 'quote', plural: true, capital: true },
        actionVocab: { noun: 'quote', plural: true, capital: false, template: 'Open {w}' },
        gate: (role, _isAdmin, o) => can(role, 'quotes.view', { overrides: o }),
      },
      {
        id: 'sales_pipeline',
        label: 'Pipeline',
        description: 'See every deal on a board, drag cards through your stages.',
        to: '/dashboard/pipelines',
        keywords: ['deals', 'kanban', 'opportunities'],
        icon: Kanban,
        badgeKey: 'unconverted_won_opportunities',
        primaryActionLabel: 'Open pipeline',
        vocab: { noun: 'pipeline', plural: false, capital: true },
        actionVocab: { noun: 'pipeline', plural: false, capital: false, template: 'Open {w}' },
        gate: (role, _isAdmin, o) => can(role, 'opportunities.view', { overrides: o }),
      },
      {
        id: 'sales_map',
        label: 'Map',
        description:
          'Your leads, door knocking, and territories on one map. Drop pins, book at the door, and see who covers each hood.',
        to: '/dashboard/lead-map',
        keywords: ['territory', 'canvass', 'door knocking'],
        icon: MapPinned,
        primaryActionLabel: 'Open map',
        gate: (role, _isAdmin, o) => can(role, 'canvass.use', { overrides: o }),
      },
      {
        id: 'sales_analytics',
        label: 'Analytics',
        description: 'How your sales operation is performing: revenue, pipeline, and reps.',
        to: '/dashboard/sales/analytics',
        keywords: ['analytics', 'reports', 'leaderboard', 'performance', 'reps'],
        icon: BarChart3,
        primaryActionLabel: 'Open sales analytics',
        gate: (role, _isAdmin, o) => can(role, 'opportunities.view', { overrides: o }),
      },
    ],
  },
  {
    id: 'customers',
    keywords: ['contacts', 'clients', 'crm'],
    label: 'Customers',
    description: 'Your customer list, your price book, service plans, agreements, and imports.',
    to: '/dashboard/customers',
    icon: Users,
    vocab: { noun: 'customer', plural: true, capital: true },
    children: [
      {
        id: 'customers_customers',
        label: 'Customers',
        description: 'Your full customer list. Add, edit, look up by name or phone.',
        to: '/dashboard/customers',
        icon: Users,
        primaryActionLabel: 'Open customers',
        vocab: { noun: 'customer', plural: true, capital: true },
        actionVocab: { noun: 'customer', plural: true, capital: false, template: 'Open {w}' },
      },
      {
        id: 'customers_services',
        label: 'Services',
        description: 'Your price book. These fill quote and invoice lines with one tap.',
        to: '/dashboard/services',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'quotes.view', { overrides: o }),
        icon: BookOpen,
        primaryActionLabel: 'Open services',
      },
      {
        id: 'customers_service_plans',
        label: 'Service plans',
        description: 'Recurring offerings: a charge on a cadence, plus optional repeat visits.',
        to: '/dashboard/plans',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'service_plans.manage', { overrides: o }),
        icon: Calendar,
        primaryActionLabel: 'Open plans',
      },
      {
        id: 'customers_agreements',
        label: 'Agreements',
        description:
          'Build reusable agreements your customers sign and plain documents you share with them.',
        to: '/dashboard/settings/agreements',
        icon: FileSignature,
        primaryActionLabel: 'Open agreements',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'customers_import',
        label: 'Import',
        description:
          'Paste a list, drop a PDF, or screenshot a page and we extract clean customer rows.',
        to: '/dashboard/customers/import',
        keywords: ['csv', 'upload', 'smart import'],
        icon: Upload,
        accent: 'ai',
        primaryActionLabel: 'Start an import',
        gate: (role, _isAdmin, o) => can(role, 'customers.import', { overrides: o }),
        surfacedOnRoutes: ['/dashboard/customers'],
      },
    ],
  },
  // Money: MERGED from the old "Money coming in" and "What you pay". The
  // section has NO gate of its own, so it is visible when at least one child
  // gate passes (filterSectionsByRole). Every child keeps its own existing
  // gate unchanged: no widening, no new section-level capability.
  {
    id: 'money',
    keywords: ['payments', 'payouts', 'invoices', 'revenue', 'get paid', 'plan', 'usage', 'fees'],
    label: 'Money',
    description: 'Invoices, payouts, revenue, your payment account, and what you pay Ghost CRM.',
    to: '/dashboard/invoices',
    icon: HandCoins,
    children: [
      {
        id: 'money_invoices',
        label: 'Invoices',
        description: 'Bill customers for completed work, track what is owed, and get paid.',
        to: '/dashboard/invoices',
        keywords: ['bill'],
        icon: Receipt,
        primaryActionLabel: 'Open invoices',
        vocab: { noun: 'invoice', plural: true, capital: true },
        actionVocab: { noun: 'invoice', plural: true, capital: false, template: 'Open {w}' },
        gate: (role, _isAdmin, o) => can(role, 'invoices.view', { overrides: o }),
      },
      {
        id: 'money_payouts',
        label: 'Payouts',
        description: 'Cash out your available balance and see your payout schedule and history.',
        to: '/dashboard/payouts',
        keywords: ['cash out', 'withdraw', 'bank'],
        icon: Wallet,
        primaryActionLabel: 'Open payouts',
        gate: (role, _isAdmin, o) => can(role, 'money.section.view', { overrides: o }),
      },
      {
        id: 'money_revenue',
        label: 'Revenue',
        description: 'Your collected revenue, top customers, and every payment you have taken.',
        to: '/dashboard/analytics/revenue',
        // PAYMENTS-LIST-1 — 'payments' was missing, and it is the word a
        // merchant actually types. The Money SECTION carried it, so a search
        // found the section and not the page holding the payments. No new menu
        // row: this page is already reachable as Money then Revenue, and a
        // second row pointing at the same page is a menu that lies about how
        // much is in it.
        keywords: ['revenue', 'sales', 'analytics', 'mrr', 'payments', 'paid', 'transactions'],
        icon: TrendingUp,
        primaryActionLabel: 'Open revenue',
        gate: (role, _isAdmin, o) => can(role, 'billing.view', { overrides: o }),
      },
      {
        id: 'money_payment_account',
        label: 'Payment account',
        description: 'Connect your payment account so customers can pay you online.',
        to: '/dashboard/settings/payments',
        keywords: ['processor', 'connect'],
        icon: ShieldCheck,
        primaryActionLabel: 'Open payment account',
        gate: (role, _isAdmin, o) => can(role, 'payment_account.view', { overrides: o }),
      },
    ],
  },
  // Website: NEW category. Builder and Analytics both moved out of Marketing.
  // No section gate: visible when either child is visible.
  {
    id: 'website',
    keywords: ['website', 'site builder', 'analytics', 'visitors', 'publish'],
    label: 'Website',
    description: 'Build your website and see who visits it.',
    to: '/dashboard/sites',
    icon: Globe,
    children: [
      {
        id: 'website_builder',
        label: 'Builder',
        description: 'Build and publish your business website.',
        to: '/dashboard/sites',
        keywords: ['sites', 'website builder', 'ai website', 'publish'],
        icon: Globe,
        primaryActionLabel: 'Open builder',
        gate: (role, isAdmin, o) =>
          sitesBuilderVisible(isAdmin) && can(role, 'marketing.page_edit', { overrides: o }),
      },
      {
        id: 'website_analytics',
        label: 'Analytics',
        description: 'See who visits your website and what they look at.',
        to: '/dashboard/analytics',
        icon: BarChart3,
        primaryActionLabel: 'Open analytics',
        gate: (role, _isAdmin, o) => can(role, 'marketing.page_analytics', { overrides: o }),
      },
    ],
  },
  // Marketing: demand-side motions. Website builder and website analytics
  // moved to the Website category this wave.
  {
    id: 'marketing',
    keywords: ['campaigns', 'email', 'reviews', 'ads', 'seo'],
    label: 'Marketing',
    description: 'Campaigns, reviews, winning back cold leads, and AI generated brand assets.',
    to: '/dashboard/marketing',
    icon: Megaphone,
    children: [
      {
        id: 'marketing_return',
        label: 'Return',
        description: 'See what every marketing channel gives back for what you spend.',
        to: '/dashboard/marketing/return',
        icon: TrendingUp,
        primaryActionLabel: 'Open return',
        gate: (role, _isAdmin, o) => can(role, 'money.section.view', { overrides: o }),
      },
      // DASH-PRESETS-1: Channels retired into the Home switcher. Its entry is
      // gone rather than redirected, because a nav item that bounces you to a
      // different section reads as a broken link. Return STAYS: it is the
      // cross-channel table, a different question from "how is this one
      // channel doing", and nothing replaced it.
      {
        id: 'marketing_campaigns',
        label: 'Campaigns',
        description:
          'Compose and send email campaigns to your audience segments. Track opens, clicks, and unsubscribes.',
        to: '/dashboard/marketing/campaigns',
        icon: Mail,
        primaryActionLabel: 'Open campaigns',
        gate: (role, _isAdmin, o) => can(role, 'marketing.campaigns_manage', { overrides: o }),
      },
      {
        id: 'marketing_reactivation',
        label: 'Reactivation',
        description: 'Win back cold leads with Ghost outreach before they go elsewhere.',
        to: '/dashboard/reactivation',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'leads.view', { overrides: o }),
        keywords: ['win back', 're-engage'],
        icon: RefreshCw,
        primaryActionLabel: 'Open reactivation',
      },
      {
        id: 'marketing_reviews',
        label: 'Reviews',
        description:
          'Triage private feedback from customers who rated below your star threshold. Follow up for service recovery.',
        to: '/dashboard/reviews',
        keywords: ['reputation', 'ratings', 'google'],
        icon: Star,
        primaryActionLabel: 'Open triage',
        gate: (role, _isAdmin, o) => can(role, 'reviews.triage', { overrides: o }),
      },
      {
        id: 'marketing_review_settings',
        label: 'Review settings',
        description:
          'Configure automated review requests after job completion. Route happy customers to Google; capture negative feedback privately.',
        to: '/dashboard/settings/reputation',
        icon: SettingsIcon,
        primaryActionLabel: 'Open review settings',
        gate: (role, _isAdmin, o) => can(role, 'reviews.settings_manage', { overrides: o }),
      },
      {
        id: 'marketing_audiences',
        label: 'Audiences',
        description:
          'Define reusable audience segments that filter which records your automated workflows act on.',
        to: '/dashboard/marketing/audiences',
        icon: Filter,
        primaryActionLabel: 'Open audiences',
        gate: (role, _isAdmin, o) => can(role, 'marketing.campaigns_manage', { overrides: o }),
      },
      {
        id: 'marketing_ai_studio',
        label: 'AI studio',
        description: 'Generate logos, cover photos, team shots, and marketing copy.',
        to: '/dashboard/ai',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'ai.tools_use', { overrides: o }),
        icon: Wand2,
        accent: 'ai',
        primaryActionLabel: 'Open AI studio',
      },
      {
        id: 'marketing_ai_history',
        label: 'AI history',
        description: 'Every AI generation you have run, ready to download or reuse.',
        to: '/dashboard/ai/history',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'ai.tools_use', { overrides: o }),
        icon: ImageIcon,
        accent: 'ai',
        primaryActionLabel: 'View history',
      },
    ],
  },
  {
    // AGENTS-RENAME-1: the LABEL is Agents. The id stays 'automations' because
    // every account_navigation row and every capability key is keyed on it, and
    // renaming those silently across live scheduled work is how things break at
    // 3am. 'automations' stays in keywords so a merchant who searches the word
    // they used yesterday still lands here.
    id: 'automations',
    keywords: ['automations', 'workflows', 'triggers', 'sequences', 'flows'],
    label: 'Agents',
    description: 'Work that runs on a schedule or a trigger, without you.',
    to: '/dashboard/automations',
    icon: Workflow,
    gate: (role, _isAdmin, o) => can(role, 'automations.view', { overrides: o }),
    children: [
      {
        id: 'automations_automations',
        label: 'Agents',
        description: 'Work that runs on a schedule or a trigger, without you.',
        to: '/dashboard/automations',
        icon: Workflow,
        keywords: ['automations', 'workflows'],
        gate: (role, _isAdmin, o) => can(role, 'automations.view', { overrides: o }),
        primaryActionLabel: 'Open agents',
      },
      {
        // NAV-MODEL: Connections lives here only now. It used to also appear
        // under Settings; that duplicate was removed. Route and gate unchanged.
        id: 'automations_connections',
        label: 'Connections',
        description:
          'Every lead source in one place: Facebook lead ads, Google lead ads, webhooks, and their health.',
        to: '/dashboard/settings/connections',
        keywords: ['integrations', 'facebook', 'google', 'webhooks', 'lead sources', 'health'],
        icon: Link2,
        primaryActionLabel: 'Open connections',
        gate: (role, _isAdmin, o) => can(role, 'settings.integrations_manage', { overrides: o }),
      },
    ],
  },
  // Team: the people on your team and how they earn.
  {
    // MARKETPLACE-MENU-1 — THE FEATURE MARKETPLACE, TOP LEVEL, DIRECTLY BELOW
    // THE AI SYSTEM.
    //
    // MARKETPLACE-BROWSE-1 put it under Agents as a child and said why: the
    // server mirror `supabase/functions/_shared/sectionRegistry.ts` is a
    // PROJECTION of the top-level sections with a test that recomputes it, and
    // that lane could not touch anything under supabase/functions. This one
    // can, so the item is where the owner asked for it and the projection was
    // RECOMPUTED BY THAT TEST rather than edited by hand.
    //
    // NO CHILDREN, ON PURPOSE. It is one screen, so `menuCatalogue` declares it
    // with a `dayOne` reason rather than "a section, derived from its children"
    // — there are none to derive it from, and the two answers may not both
    // apply to one id.
    //
    // ACCOUNT GATED EXACTLY AS IT WAS: the same capability the route carries,
    // so the row and the screen agree about who may use it.
    // MARKETPLACE-HIDDEN-1 — AND IT IS NOW HIDDEN ON PURPOSE, ruled by the
    // owner on 9 September 2026. The row is gone for every role at every
    // width; the routes, the page and the module all stay exactly where they
    // are. `deadSections.ts` carries the ruling in words, and a guard reads it
    // back, so the next person to find this missing reads a decision.
    id: 'marketplace',
    keywords: ['marketplace', 'features', 'add ons', 'addons', 'store', 'browse', 'shop'],
    label: 'Feature Marketplace',
    description: 'Everything Ghost can be. Browse features, search, and switch on what you need.',
    to: '/dashboard/marketplace',
    icon: Store,
    gate: () => !sectionIsHidden('marketplace'),
    children: [],
  },
  {
    id: 'team',
    keywords: ['staff', 'members', 'commissions', 'payroll', 'employees'],
    label: 'Team',
    description: 'The people on your team and how they earn.',
    to: '/dashboard/team',
    icon: Users,
    children: [
      {
        id: 'team_members',
        label: 'Members',
        description: 'Invite teammates, change roles, remove people who leave.',
        to: '/dashboard/settings/team',
        keywords: ['members', 'roster', 'staff', 'roles'],
        icon: Users,
        primaryActionLabel: 'Manage members',
        gate: (role, _isAdmin, o) => can(role, 'team.view', { overrides: o }),
      },
      {
        id: 'team_leaderboard',
        label: 'Leaderboard',
        description: 'Who is winning the week: doors, appointments, sales, and competitions.',
        to: '/dashboard/leaderboard',
        icon: Trophy,
        primaryActionLabel: 'See the board',
        gate: (role, _isAdmin, o) => can(role, 'leaderboard.view', { overrides: o }),
      },
      {
        id: 'team_commissions',
        label: 'Commissions',
        description: 'Configure how team members earn on closed deals.',
        to: '/dashboard/settings/commissions',
        icon: Coins,
        primaryActionLabel: 'Manage rules',
        gate: (role, _isAdmin, o) => can(role, 'commissions.rules_manage', { overrides: o }),
      },
      {
        id: 'team_payroll',
        label: 'Payroll',
        description: 'See what you owe your team this period. Close the period and mark it paid.',
        to: '/dashboard/payroll',
        keywords: ['wages', 'pay'],
        icon: Banknote,
        primaryActionLabel: 'Open payroll',
        gate: (role, _isAdmin, o) => can(role, 'payroll.view_dashboard', { overrides: o }),
      },
      {
        id: 'team_my_commissions',
        label: 'My commissions',
        description: 'See what you have earned, what is vested, and what has been paid.',
        to: '/dashboard/my-commissions',
        icon: DollarSign,
        primaryActionLabel: 'View earnings',
        gate: (role, _isAdmin, o) => can(role, 'commissions.view_own', { overrides: o }),
      },
      {
        id: 'team_my_paychecks',
        label: 'My paychecks',
        description: 'Every paycheck since your payroll started. Drill in for the full breakdown.',
        to: '/dashboard/my-paychecks',
        icon: Banknote,
        primaryActionLabel: 'View paychecks',
        gate: (role, _isAdmin, o) => can(role, 'paychecks.view_own', { overrides: o }),
      },
    ],
  },
  {
    id: 'settings',
    keywords: ['account', 'preferences', 'config'],
    label: 'Settings',
    description: "Your account, your team's tools, and how the app behaves.",
    to: '/dashboard/settings',
    icon: SettingsIcon,
    children: [
      {
        id: 'settings_account',
        label: 'Account',
        description: 'Your profile, username, email, password, and notification preferences.',
        to: '/dashboard/settings',
        icon: SettingsIcon,
        primaryActionLabel: 'Open account',
      },
      // THE-DIVIDER-HOLDS-ONLY-WHAT-IS-RULED-1 — THE WALLET AND THE BILL LIVE IN
      // SETTINGS. Money is no longer fixed furniture: it stands above the divider
      // only for an account that had a money surface built. The two pages that
      // are nobody's build — the plan and credit balance, and the fee tier — move
      // here so they are reachable on every account, keeping the ids, the labels
      // and the routes they already had.
      {
        id: 'money_plan_and_usage',
        label: 'Plan and usage',
        description: 'Your plan, this month of usage, and your auto charge and spending controls.',
        to: '/dashboard/money/plan-and-usage',
        keywords: ['plan', 'wallet', 'credits', 'balance', 'usage'],
        icon: WalletMinimal,
        primaryActionLabel: 'Open plan and usage',
        gate: (role, _isAdmin, o) => can(role, 'billing.view', { overrides: o }),
      },
      {
        id: 'money_fees',
        label: 'Fees',
        description: 'Your processing fee tier, monthly progress, and next tier savings.',
        to: '/dashboard/billing/fees',
        keywords: ['fee', 'tier', 'processing', 'volume'],
        icon: Coins,
        primaryActionLabel: 'Open fees',
        gate: (role, _isAdmin, o) => can(role, 'billing.view', { overrides: o }),
      },
      {
        id: 'settings_branding',
        label: 'Branding',
        description:
          'Logo, brand colors, voice, default design system, and email signature. Shows up on your customer portal and AI generated sites.',
        to: '/dashboard/settings/branding',
        icon: Palette,
        primaryActionLabel: 'Open branding',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_themes',
        label: 'Themes',
        description:
          'How your quotes, invoices, and customer portal look. Edit colours, fonts, and your logo with a live preview.',
        to: '/dashboard/settings/themes',
        icon: Palette,
        primaryActionLabel: 'Open themes',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        // PLATFORM-1 — the menu editor. Gated on the same capability the route
        // and the table's write policy both use.
        id: 'settings_menu',
        label: 'Menu',
        description:
          'Hide what this business does not use, rename anything you call something else, and put the important things first.',
        to: '/dashboard/settings/navigation',
        icon: LayoutDashboard,
        primaryActionLabel: 'Open menu settings',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        // TEMPLATES-1 — the add-ons library. A module IS a navigation section,
        // so this page and the menu editor write the same rows.
        id: 'settings_modules',
        label: 'What this can do',
        description:
          'Switch on the parts of the product you use. Switching one off only takes it off the menu.',
        to: '/dashboard/settings/modules',
        icon: Boxes,
        primaryActionLabel: 'Open what this can do',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        // RECORD-TYPES-1 — the type editor. Fields for a custom type are added
        // on the row below, because a custom type's fields ARE custom fields.
        id: 'settings_record_types',
        label: 'Your own record types',
        description:
          'Records this product has never had. They get their own screens, their own fields, and Ghost can read them.',
        to: '/dashboard/settings/record-types',
        icon: Boxes,
        primaryActionLabel: 'Open your own record types',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        // CUSTOM-FIELDS-1 — the field editor. Same gate as the route and the
        // table's RLS policy, so the nav never offers what the page refuses.
        id: 'settings_custom_fields',
        label: 'Your own fields',
        description:
          'Add the questions your business asks that ours does not. They appear on the form, on the record, and Ghost can read them.',
        to: '/dashboard/settings/custom-fields',
        icon: ClipboardCheck,
        primaryActionLabel: 'Open your own fields',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        // SANDBOX-1 — merchant artifacts. Listed here so the page is reachable
        // by exactly the members who may manage settings, using the same gate
        // the route and the table's RLS policy both apply.
        id: 'settings_artifacts',
        label: 'Artifacts',
        description:
          'Things built for this business, each rendered in a sandbox that runs its own code and reaches nothing else.',
        to: '/dashboard/settings/artifacts',
        icon: Boxes,
        primaryActionLabel: 'Open artifacts',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_business_hours',
        label: 'Business hours',
        description: 'Set your weekly schedule and any holiday or special day overrides.',
        to: '/dashboard/settings/business-hours',
        icon: ClockIcon,
        primaryActionLabel: 'Open hours',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_service_area',
        label: 'Service area',
        description: 'Define where you take jobs: radius from your home base, ZIP list, or cities.',
        to: '/dashboard/settings/service-area',
        icon: MapPin,
        primaryActionLabel: 'Open service area',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_calendar_sync',
        label: 'Calendar sync',
        description: 'Connect Google Calendar so customers can book during the hours you set.',
        to: '/dashboard/account/calendar',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'schedule.view_own', { overrides: o }),
        icon: Calendar,
        primaryActionLabel: 'Open calendar sync',
      },
      {
        id: 'settings_notifications',
        label: 'Notifications',
        description:
          'Choose when Ghost emails you about new leads and other activity in this account.',
        to: '/dashboard/settings/notifications',
        icon: Bell,
        primaryActionLabel: 'Open notification settings',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_messages',
        label: 'Messages',
        description:
          'The automatic emails and texts sent to your customers. Edit the wording or keep the defaults.',
        to: '/dashboard/settings/messages',
        icon: Mail,
        primaryActionLabel: 'Open messages',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_your_words',
        label: 'Your words',
        description:
          'Rename what Ghost calls things like jobs, quotes, technicians, visits, and your job statuses. The built in words are the default.',
        to: '/dashboard/settings/vocabulary',
        icon: BookOpen,
        primaryActionLabel: 'Open your words',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_public_quote',
        label: 'Public quote',
        description:
          'What customers see when they open a quote link: your personal message, testimonials, and what happens after they accept.',
        to: '/dashboard/settings/quote-page',
        icon: FileText,
        primaryActionLabel: 'Open public quote settings',
        vocab: { noun: 'quote', plural: false, capital: false, template: 'Public {w}' },
        actionVocab: {
          noun: 'quote',
          plural: false,
          capital: false,
          template: 'Open public {w} settings',
        },
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_customer_portal',
        label: 'Customer portal',
        vocab: { noun: 'customer', plural: false, capital: true, template: '{w} portal' },
        description:
          'Choose which sections customers see when they open their portal link, and the order they appear in.',
        to: '/dashboard/settings/portal',
        icon: LayoutDashboard,
        primaryActionLabel: 'Open portal settings',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_lead_sources',
        label: 'Lead sources',
        vocab: { noun: 'lead', plural: false, capital: true, template: '{w} sources' },
        description:
          'Where your leads come from. Add your own sources; the built in ones are shared and read only.',
        to: '/dashboard/settings/lead-sources',
        icon: Megaphone,
        actionVocab: { noun: 'lead', plural: false, capital: false, template: 'Open {w} sources' },
        primaryActionLabel: 'Open lead sources',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_canvass_dispositions',
        label: 'Canvass dispositions',
        description:
          'The outcomes reps tap after a knock, and the flags that decide how each one counts in your stats.',
        to: '/dashboard/settings/canvass-dispositions',
        icon: MapPin,
        primaryActionLabel: 'Open canvass dispositions',
        gate: (role, _isAdmin, o) => can(role, 'settings.account_manage', { overrides: o }),
      },
      {
        id: 'settings_pay_periods',
        label: 'Pay periods',
        description: 'Pick how often you pay your team and when each pay period starts.',
        to: '/dashboard/settings/pay-periods',
        icon: Calendar,
        primaryActionLabel: 'Open pay period settings',
        gate: (role, _isAdmin, o) => can(role, 'payroll.manage_periods', { overrides: o }),
      },
      {
        id: 'settings_bonuses',
        label: 'Bonuses',
        description: 'Reward your team for reviews, referrals, hitting goals, or great work.',
        to: '/dashboard/settings/bonuses',
        icon: Gift,
        primaryActionLabel: 'Open bonuses',
        gate: (role, _isAdmin, o) => can(role, 'commissions.rules_manage', { overrides: o }),
      },
      {
        id: 'settings_affiliate_payouts',
        label: 'Affiliate payouts',
        description:
          'Connect a US bank account to receive your affiliate referral commissions via ACH direct deposit.',
        to: '/dashboard/settings/bank-account',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'payment_account.view', { overrides: o }),
        icon: Banknote,
        primaryActionLabel: 'Manage affiliate payouts',
      },
      {
        id: 'settings_import_from_another_crm',
        label: 'Import from another CRM',
        description:
          'Bring your customer list from Housecall Pro, Jobber, GoHighLevel, or any CSV. Ghost auto detects which column maps to which field.',
        to: '/dashboard/settings/import',
        icon: Upload,
        primaryActionLabel: 'Open importer',
        gate: (role, _isAdmin, o) => can(role, 'customers.import', { overrides: o }),
      },
      {
        id: 'settings_ghost',
        label: 'Ghost',
        description:
          'Turn Ghost on or off, pick which features run in the background, set a monthly budget cap, and pause anytime.',
        to: '/dashboard/settings/service-bot',
        // ROLE-GATE-1: mirrors the route capability so the nav never
        // shows a link that the page then refuses.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'settings.account_manage', { overrides: o }),
        icon: Bot,
        primaryActionLabel: 'Open Ghost settings',
      },
      {
        // THE-ACCOUNT-REMEMBERS-2 — the memory has a row of its own, beside
        // Ghost's settings rather than buried inside them, because a merchant
        // looking for what Ghost knows about them is not looking for a toggle.
        id: 'settings_memory',
        label: 'What Ghost remembers',
        description:
          'Everything Ghost has learned about your business, used in every chat. Confirm what is right and tell it to forget what is not.',
        to: '/dashboard/settings/memory',
        // ROLE-GATE-1: mirrors the route capability so the nav never shows a
        // link the page then refuses. Unchanged from the page it moved off.
        gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
          can(role, 'settings.account_manage', { overrides: o }),
        icon: Brain,
        primaryActionLabel: 'See what Ghost remembers',
      },
      {
        id: 'settings_affiliate_program',
        label: 'Affiliate program',
        description: 'Share your link and earn 30% of our revenue from every business you refer.',
        to: '/dashboard/affiliate',
        icon: Share2,
        primaryActionLabel: 'Open affiliate program',
      },
      // Feature-flagged social settings stay at the end of Settings.
      ...(features.whatsapp
        ? [
            {
              id: 'settings_whatsapp',
              label: 'WhatsApp',
              description:
                'Manage WhatsApp message templates, view conversation stats, and configure the Twilio sender.',
              to: '/dashboard/settings/whatsapp',
              icon: MessageCircle,
              primaryActionLabel: 'Open WhatsApp settings',
              gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
                can(role, 'settings.integrations_manage', { overrides: o }),
            },
          ]
        : []),
      ...(features.messenger || features.instagram
        ? [
            {
              id: 'settings_meta_integrations',
              label: 'Meta integrations',
              description:
                'Connect Facebook Pages for Messenger and Instagram accounts for DMs. Manage OAuth tokens and webhook configuration.',
              to: '/dashboard/settings/integrations/meta',
              icon: Link2,
              primaryActionLabel: 'Open Meta connections',
              gate: (role: TeamRole | null, _isAdmin: boolean, o?: CapabilityOverrides | null) =>
                can(role, 'settings.integrations_manage', { overrides: o }),
            },
          ]
        : []),
    ],
  },
  {
    id: 'admin',
    keywords: ['platform', 'superadmin'],
    label: 'Admin',
    description: 'Ghost CRM platform admin tools.',
    to: '/dashboard/admin',
    icon: Crown,
    accent: 'admin',
    gate: (_role, isAdmin) => isAdmin === true,
    children: [
      // ADMIN-IA-1 — FOUR destinations, grouped by what an admin is doing.
      //
      // It was thirteen in a horizontally scrolling strip, which meant the last
      // four were invisible and the first four looked more important than they
      // were. Grouping is by TASK, not by which table a page reads:
      //
      //   Accounts    who is on the platform and what they are worth. Platform
      //               totals live HERE, above the list, because a platform
      //               number you cannot click into is trivia.
      //   Affiliates  the whole referral money chain in one place.
      //   Onboarding  how new merchants are getting on, and who is helping.
      //   Pricing     what things cost us and what we charge.
      //
      // Anything touched monthly or less (bonus events, nurture sequences, demo
      // accounts, workflows, audiences) is reachable from within its section
      // rather than taking permanent top level space.
      {
        id: 'admin_accounts',
        label: 'Accounts',
        description: 'Platform totals and every account on the platform.',
        to: '/dashboard/admin/accounts',
        icon: Building2,
        accent: 'admin',
        primaryActionLabel: 'Find an account',
      },
      {
        id: 'admin_affiliates',
        label: 'Affiliates',
        description: 'Affiliates, what they are owed, commission events and clawbacks.',
        to: '/dashboard/admin/affiliates',
        icon: HandCoins,
        accent: 'admin',
        primaryActionLabel: 'Open affiliates',
      },
      {
        id: 'admin_onboarding',
        label: 'Onboarding',
        description: 'Activation funnel, onboarding analytics and the specialist roster.',
        to: '/dashboard/admin/activation-funnel',
        icon: ShieldCheck,
        accent: 'admin',
        primaryActionLabel: 'Open onboarding',
      },
      {
        id: 'admin_health',
        label: 'Health',
        description: 'Every scheduled job, its last run, and anything that needs attention.',
        to: '/dashboard/admin/health',
        icon: ShieldCheck,
        accent: 'admin',
        primaryActionLabel: 'Open health',
      },
      {
        id: 'admin_pricing',
        label: 'Pricing',
        description: 'The cost dictionary and AI pricing rules.',
        to: '/dashboard/admin/cost-dictionary',
        icon: Banknote,
        accent: 'admin',
        primaryActionLabel: 'Open pricing',
      },
    ],
  },
  // CONNECT-2 — THE BOTTOM OF THE NAVIGATION, AND A PAGE RATHER THAN A TAB.
  //
  // Last in the array so it is last on screen for everybody: Admin above it is
  // gated to admins, so for a merchant this is the final item.
  {
    id: 'connectors',
    keywords: ['integrations', 'connect', 'apps', 'services', 'nango'],
    label: 'Connectors',
    description: 'Connect the services you already use so Ghost can read from them.',
    to: '/dashboard/connectors',
    icon: Link2,
    children: [],
  },
]

// ---------- helpers ----------

// Keep gate evaluation lenient on null role (mid-load): we treat
// it as "show by default" so the user does not see a flash of
// missing nav items while their team_members row resolves.
function passesGate(
  gate: NavSection['gate'] | NavChild['gate'] | undefined,
  role: TeamRole | null,
  isAdmin: boolean,
  overrides?: CapabilityOverrides | null,
): boolean {
  if (!gate) return true
  if (role === null) return true
  return gate(role, isAdmin, overrides)
}

// A section is visible when its own gate passes AND, for a multi-child
// section, at least one child is visible. A section with NO gate but with
// children (Money, Website) therefore shows exactly when one of its children
// is visible, which is the merged-Money gate rule: visible if the user passes
// the gate of at least one child, with every child keeping its own gate.
export function filterSectionsByRole(
  sections: NavSection[],
  role: TeamRole | null,
  isAdmin: boolean,
  overrides?: CapabilityOverrides | null,
): NavSection[] {
  return sections.filter((s) => {
    // MARKETPLACE-HIDDEN-1 — A SECTION HIDDEN ON PURPOSE IS HIDDEN BEFORE THE
    // GATE IS ASKED, because `passesGate` lets a null role through on purpose
    // (a menu that is still learning who you are shows everything rather than
    // flashing a smaller one). That is right for a restriction and wrong for a
    // dead section: a thing that is gone is gone while the role loads too.
    // Every menu surface reads this one function — desktop sidebar, side
    // drawer, mobile bottom bar — so this is every width and every role.
    if (sectionIsHidden(s.id)) return false
    if (!passesGate(s.gate, role, isAdmin, overrides)) return false
    // Single-page sections (children: []) -- e.g. Home -- are always
    // visible when their gate passes. The "zero clickable rows" hide
    // only applies to multi-child sections where every child got
    // gated out for this user.
    if (s.children.length === 0) return true
    const visibleChildren = filterChildrenByRole(s.children, role, isAdmin, overrides)
    return visibleChildren.length > 0
  })
}

export function filterChildrenByRole(
  children: NavChild[],
  role: TeamRole | null,
  isAdmin: boolean,
  overrides?: CapabilityOverrides | null,
): NavChild[] {
  return children.filter((c) => passesGate(c.gate, role, isAdmin, overrides))
}

// Phase A2: pick the route a sidebar section row should navigate to when the
// user clicks it. Sends users straight to the first child they can access.
// Falls back to `section.to` if the role gate hides every child.
export function getFirstChildPath(
  section: NavSection,
  role: TeamRole | null,
  isAdmin: boolean,
  overrides?: CapabilityOverrides | null,
): string {
  const visibleChildren = filterChildrenByRole(section.children, role, isAdmin, overrides)
  return visibleChildren[0]?.to ?? section.to
}

// Phase 49 Stage 4: the first section path a role can actually view. Used as a
// landing fallback so a restricted role that hits a section they cannot view is
// sent somewhere they can (Home is ungated, so in practice this returns
// /dashboard for everyone; kept general in case Home is ever gated).
/**
 * PLATFORM-1 — LEFT READING THE RAW TREE, AND IT HAS NO CALLERS.
 *
 * Checked rather than assumed: nothing in src imports this. It is left as it
 * is because changing a function nobody calls is churn, and because it could
 * not return a hidden section anyway - the first role-visible section is
 * always Home, which no account may hide. If it ever gains a caller it must
 * take the resolved tree, exactly like the two below it.
 */
export function firstVisibleSectionPath(
  role: TeamRole | null,
  isAdmin: boolean,
  overrides?: CapabilityOverrides | null,
): string {
  const visible = filterSectionsByRole(SECTIONS, role, isAdmin, overrides)
  const first = visible[0]
  if (!first) return '/dashboard'
  return getFirstChildPath(first, role, isAdmin, overrides)
}

// Match the section that "owns" a given pathname. Used by both
// sidebars to expand the active section automatically. Picks the
// most specific section by `to` length so /dashboard/settings/team
// matches the Team section (children path) before the Settings
// section (whose `to` is /dashboard/settings).
/**
 * PLATFORM-1 — TAKES THE RESOLVED TREE.
 *
 * This read the raw SECTIONS constant, which meant it could return a section
 * the account has hidden - so a sidebar would highlight a module the merchant
 * removed. Callers pass the tree from useNavSections, which already carries
 * the merchant's vocabulary AND their menu configuration.
 */
export function findSectionForPath(
  sections: ReadonlyArray<NavSection>,
  pathname: string,
): NavSection | null {
  let best: NavSection | null = null
  let bestLen = 0
  for (const s of sections) {
    // Children of this section may live under sibling URLs
    // (e.g. Team section's "Members" lives at /dashboard/settings/team).
    // Check children first so they win regardless of `s.to`.
    for (const c of s.children) {
      if (pathname === c.to || pathname.startsWith(c.to + '/')) {
        if (c.to.length > bestLen) {
          best = s
          bestLen = c.to.length
        }
      }
    }
    if (pathname === s.to || pathname.startsWith(s.to + '/')) {
      if (s.to.length > bestLen) {
        best = s
        bestLen = s.to.length
      }
    }
  }
  return best
}

/**
 * NAV-1: the single active child among a sibling set, or null. Moved here
 * from SectionTabs (AB-1) so BOTH nav systems (top pills + sidebars) share
 * one matcher and the pathname-only NavLink bug class stays dead.
 *
 * Rules: pathname must match exactly; among pathname matches, a child whose
 * `to` query params are ALL present (with equal values) in the current
 * search wins, most params first. A child with no query only wins when
 * nothing more specific matched, so `?tab=x` / `?channel=voice` siblings
 * light exactly one row.
 */
export function resolveActiveChildTo(
  children: NavChild[],
  pathname: string,
  search: string,
): string | null {
  const current = new URLSearchParams(search)
  let best: { to: string; specificity: number } | null = null
  for (const child of children) {
    const qIndex = child.to.indexOf('?')
    const toPath = qIndex === -1 ? child.to : child.to.slice(0, qIndex)
    if (toPath !== pathname) continue
    const toParams = new URLSearchParams(qIndex === -1 ? '' : child.to.slice(qIndex + 1))
    let allMatch = true
    let specificity = 0
    toParams.forEach((value, key) => {
      specificity += 1
      if (current.get(key) !== value) allMatch = false
    })
    if (!allMatch) continue
    if (!best || specificity > best.specificity) best = { to: child.to, specificity }
  }
  return best?.to ?? null
}

// Match the child whose route exactly equals (or is a parent of)
// the current pathname. Used to highlight the active row inside an
// expanded section.
/**
 * PLATFORM-1 — TAKES THE RESOLVED TREE, for the same reason as
 * findSectionForPath above.
 */
export function findChildForPath(
  sections: ReadonlyArray<NavSection>,
  pathname: string,
): NavChild | null {
  let best: NavChild | null = null
  let bestLen = 0
  for (const s of sections) {
    for (const c of s.children) {
      if (pathname === c.to || pathname.startsWith(c.to + '/')) {
        if (c.to.length > bestLen) {
          best = c
          bestLen = c.to.length
        }
      }
    }
  }
  return best
}

// Wave F4 NAMEPLATE: resolve one vocab binding into its display string.
// Substitutes the merchant's word into the binding's template ({w}), or the
// bare word when no template is given.
function renderNavBinding(vocab: Vocabulary, binding: NavVocabBinding): string {
  const word = vocabWord(vocab, binding.noun, {
    plural: binding.plural,
    capital: binding.capital,
  })
  return binding.template ? binding.template.replace('{w}', word) : word
}

// Wave F4 NAMEPLATE: return a copy of the sections tree with every
// vocab-bound label / primaryActionLabel swapped for the merchant's word.
// Nodes without a binding are returned by reference (untouched), so with the
// default vocabulary every string is byte-identical to today. Icons, gates,
// routes, and every other field are preserved.
export function applyVocabularyToSections(sections: NavSection[], vocab: Vocabulary): NavSection[] {
  return sections.map((section) => {
    let sectionChanged = false
    const children = section.children.map((child) => {
      if (!child.vocab && !child.actionVocab) return child
      const next: NavChild = { ...child }
      if (child.vocab) next.label = renderNavBinding(vocab, child.vocab)
      if (child.actionVocab && child.primaryActionLabel != null) {
        next.primaryActionLabel = renderNavBinding(vocab, child.actionVocab)
      }
      sectionChanged = true
      return next
    })
    // VOCAB-1: the section's OWN label follows the merchant's word too.
    const label = section.vocab ? renderNavBinding(vocab, section.vocab) : section.label
    if (label !== section.label) return { ...section, label, children }
    return sectionChanged ? { ...section, children } : section
  })
}

// ─── RECORD-TYPES-1: a business's own record types in the menu ─────────
//
// A custom type gets a navigation entry, and it flows through EXACTLY the same
// pipeline a built-in row does. This runs BEFORE the account layer in
// useResolvedNav, which is what makes the two interact correctly:
//
//   * PLATFORM-1's layer is SUBTRACT-ONLY and keyed on stable node ids, so a
//     business can hide, rename and reorder its OWN custom type rows with the
//     same editor it uses for ours. It cannot ADD one, because the layer never
//     adds - the row has to be here first, and it is here only because the
//     business defined the type.
//   * The node id derives from the SLUG (navNodeIdForType), never the label,
//     so hiding "Matters" and then renaming it to "Cases" keeps it hidden.
//     That was PLATFORM-1's whole lesson about mutable identity.
//   * The section is ABSENT when there are no types, so an account with none
//     renders a menu byte-identical to today - no empty section, no divider.
//
// The section is NOT in PERMANENT_SECTION_IDS: a business that hides its own
// records section is hiding something it created, and it can always reach the
// editor, which lives in Settings and is permanent.
export function withRecordTypeSection(
  sections: ReadonlyArray<NavSection>,
  types: ReadonlyArray<{
    slug: string
    labelOne: string
    labelMany: string
    icon: string
    removedAt: string | null
    /**
     * HIERARCHY-1 — the section this type was placed under, or null for a
     * section of its own. ABSENT ON EVERY EXISTING ROW, and null is the old
     * behaviour byte for byte, so an account that built things before this
     * wave sees exactly the menu it saw yesterday.
     */
    homeSection?: string | null
  }>,
  iconFor: (icon: string) => React.ComponentType<{ className?: string }>,
): NavSection[] {
  const live = types.filter((t) => !t.removedAt)
  // BYTE IDENTICAL when there are none: the same array contents, no section.
  if (live.length === 0) return [...sections]

  const childFor = (t: (typeof live)[number]): NavChild => ({
    // DERIVED, NOT REBUILT. The first version of this interpolated the slug
    // into a node-id prefix and a route path by hand, which is two more
    // definitions of things that already have exactly one. Both now come from
    // the mirror, and a test asserts they agree - and that test reads this
    // function's SOURCE, so the wording here stays clear of the patterns it
    // looks for.
    id: navNodeIdForType(t),
    label: t.labelMany,
    description: `Records of a type this business defined.`,
    to: listPathForType(t),
    icon: iconFor(t.icon),
    primaryActionLabel: `Open ${t.labelMany.toLowerCase()}`,
  })

  // HIERARCHY-1 — A TYPE WITH A HOME GOES UNDER THAT SECTION.
  //
  // Everything a business invented used to pile into one generic "Records"
  // drawer: a pool company's chemical log, a law firm's matters and a
  // roofer's materials, side by side. A type now says where it belongs and
  // lands there instead.
  // ONE DECISION, not two. A type goes under a section only if its home names
  // a section this account actually has. Everything else - no home at all, an
  // empty home, or a home naming something that is not there - lands in
  // Records, which is where it landed before this wave.
  //
  // This started as two checks, a "has a home" filter and an "unknown home"
  // fallback, and disabling the first one changed nothing: both routed the
  // same types to the same place, so neither could be pinned by a test. One
  // check that a test can break is worth more than two that it cannot.
  const knownIds = new Set(sections.map((s) => s.id))
  const homeOf = (t: (typeof live)[number]): string | null => {
    const h = typeof t.homeSection === 'string' ? t.homeSection : ''
    return h.length > 0 && knownIds.has(h) ? h : null
  }

  let out: NavSection[] = sections.map((s) => {
    const mine = live.filter((t) => homeOf(t) === s.id)
    if (mine.length === 0) return s
    return { ...s, children: [...s.children, ...mine.map(childFor)] }
  })

  const ownSection = live.filter((t) => homeOf(t) === null)
  if (ownSection.length > 0) {
    const children = ownSection.map(childFor)
    out = [
      ...out,
      {
        id: 'records',
        label: 'Records',
        description: 'The record types this business invented.',
        to: children[0].to,
        icon: Boxes,
        children,
      },
    ]
  }
  return out
}

// buildSections returns the canonical SECTIONS list. Kept for compatibility
// with any caller still passing the old isAgency flag; Agency and Portfolio
// are scope selectors owned by the SubAccountSwitcher, not nav sections.
export function buildSections(_isAgency?: boolean): NavSection[] {
  return SECTIONS
}

// Re-export the Briefcase icon for callers that want a generic
// page-header glyph alongside the section list. Preserves the
// pre-Phase-A export so existing consumers keep compiling.
export { Briefcase as PrimaryNavIcon }
